import { Field, Form, Formik } from "formik";
import React, { useState } from "react";

import ButtonLoader from "@/Components/ButtonLoader";
import { EyeIcon } from "@/Components/Icons/EyeIcon";
import { EyeSlashIcon } from "@/Components/Icons/EyeSlashIcon";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

import { _set_new_password } from "../../../../services/auth";
import FieldErrorMessage from "../../FieldErrorMessage";
import { RequiredSymbol } from "../../RequiredSymbol";

/**
 * ChangePassword component for changing user's password.
 *
 * @return {JSX.Element} The JSX for the ChangePassword component.
 */
const ChangePassword = () => {
  /**
   * Validates the format of the password using a regular expression.
   *
   * @param {string} password - The password to be validated
   * @return {boolean} true if the password format is valid, otherwise false
   */
  const validatePasswordFormat = (password) => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  return (
    <div className="ChangePassword_Wrapper tour-reset-clientuser-password mb-14">
      <div className="shadow-[0px_1px_8px_0px_#E7EFFB rounded-[10px] bg-white px-5 py-6">
        <h1 className="pb-6 text-[20px] font-semibold leading-[normal] text-black">
          Change Password
        </h1>
        <Formik
          initialValues={{
            old_password: "",
            new_password: "",
            confirm_password: "",
            password_action: "ChangePassword",
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validateOnMount={false}
          validate={(values) => {
            const errors = {};
            if (!values.old_password) {
              errors.old_password = "Current Password is Required";
            }
            if (!values.new_password) {
              errors.new_password = "New Password is Required";
            } else {
              if (!validatePasswordFormat(values.new_password)) {
                errors.new_password =
                  "Password must contain at least 8 characters, including uppercase, lowercase letters, numbers, and special characters";
              }
            }
            if (!values.confirm_password) {
              errors.confirm_password = "Confirm Password is Required";
            }
            if (values.old_password == values.new_password) {
              errors.new_password =
                "New Password should not be same as old password";
            }
            if (values.new_password !== values.confirm_password) {
              errors.confirm_password = "Password does not match";
            }

            return errors;
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            let response = _set_new_password(values);
            response
              .then((res) => {
                // console.log("res =>", res);
                const {
                  data: { message },
                } = res;
                resetForm();
                showSuccessToast(message);
              })
              .catch((error) => {
                handleAPICatch(error);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            submitForm,
            isSubmitting,
            errors,
            touched,
            isValid,
            values,
            resetForm,
          }) => (
            <Form>
              <div className="grid gap-6">
                <div className="grid grid-cols-1 items-start gap-6 sm:grid-cols-2 md:grid-cols-3">
                  <div className="grid gap-3">
                    <label
                      htmlFor="old_password"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Current Password <RequiredSymbol />
                    </label>
                    <div>
                      <div className="relative">
                        <Field
                          type={showPassword ? "text" : "password"}
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                            errors.old_password ? "!border-red-500" : ""
                          }`}
                          autoComplete="off"
                          name="old_password"
                          value={values.old_password}
                          placeholder="Enter Current Password"
                        />
                        <button
                          type="button"
                          className="absolute right-[10px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 border-none bg-none text-black"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeIcon cls="w-full h-full" />
                          ) : (
                            <EyeSlashIcon cls="w-[16px] h-[16px]" />
                          )}
                        </button>
                      </div>
                      <FieldErrorMessage
                        name={"old_password"}
                        message={errors.old_password}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3">
                    <label
                      htmlFor="new_password"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      New Password <RequiredSymbol />
                    </label>
                    <div>
                      <div className="relative">
                        <Field
                          type={showNewPassword ? "text" : "password"}
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                            errors.new_password ? "!border-red-500" : ""
                          }`}
                          autoComplete="off"
                          name="new_password"
                          value={values.new_password}
                          placeholder="Enter New Password"
                        />
                        <button
                          type="button"
                          className="absolute right-[10px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 border-none bg-none text-black"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeIcon cls="w-[16px] h-[16px]" />
                          ) : (
                            <EyeSlashIcon cls="w-[16px] h-[16px]" />
                          )}
                        </button>
                      </div>
                      <FieldErrorMessage
                        name={"new_password"}
                        message={errors.new_password}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3">
                    <label
                      htmlFor="confirm_password"
                      className="text-[14px] font-medium leading-[normal] text-black"
                    >
                      Confirm Password <RequiredSymbol />
                    </label>
                    <div>
                      <Field
                        type="password"
                        className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 text-[14px] font-medium leading-[normal] text-black !outline-none !ring-0 focus:border-primary ${
                          errors.confirm_password ? "!border-red-500" : ""
                        }`}
                        autoComplete="off"
                        name="confirm_password"
                        value={values.confirm_password}
                        placeholder="Enter Confirm Password"
                      />
                      <FieldErrorMessage
                        name={"confirm_password"}
                        message={errors.confirm_password}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-5">
                  <button
                    className="transition-linear relative h-[40px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium leading-[normal] text-white hover:bg-white hover:text-primary"
                    onClick={() => {
                      submitForm();
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <ButtonLoader /> : <span>Change</span>}
                  </button>
                  <button
                    type="button"
                    className="transition-linear h-[40px] w-[120px] rounded-lg border border-primary bg-white text-[14px] font-medium leading-[normal] text-primary hover:bg-primary hover:text-white"
                    onClick={() => {
                      resetForm();
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ChangePassword;
